import React, { useEffect, useRef, useState } from 'react';
import {
  ACCEPTED_IMAGE_ATTR,
  MAX_IMAGE_LABEL,
  MAX_PORTFOLIO_IMAGES,
  validateImage,
} from '../../lib/creatorMedia';
import {
  ALL_STEPS,
  DEFAULT_PROFILE_BG,
  EMOJI_OPTIONS,
  LAST_STEP,
  MAX_BIO_CHARS,
  MAX_TAGS,
  NICHE_OPTIONS,
  PLATFORM_OPTIONS,
  PROFILE_BUILDER_STEPS,
  ProfileFieldError,
  normalizeHandle,
  validateStep,
  validateSteps,
} from '../../lib/creatorProfile';
import type { DraftErrors, ProfileBuilderStep } from '../../lib/creatorProfile';
import type { CreatorProfileDraft, Platform } from '../../types';

/** How long typing must pause before a handle is checked against the backend. */
const HANDLE_CHECK_DEBOUNCE_MS = 400;

/** Outcome of the live handle lookup, as reflected under the handle input. */
type HandleStatus = 'idle' | 'checking' | 'available' | 'taken';

/**
 * How the same form is being used:
 *   • `create` — onboarding. One stage at a time, each persisted as it is
 *     completed, so an abandoned run resumes from the row it already made.
 *   • `edit` — an existing profile. Every field at once, saved in one write.
 *
 * Only the shape of the journey differs. The inputs, the validation and the
 * mapping to `creators` columns are literally the same code in both, which is
 * the point: a rule added for onboarding cannot quietly not apply to editing.
 */
export type ProfileBuilderMode = 'create' | 'edit';

export interface ProfileBuilderProps {
  /** Defaults to `create` — the onboarding wizard. */
  mode?: ProfileBuilderMode;
  /** Starting field values — blank for a new profile, or an existing row's. */
  initialDraft: CreatorProfileDraft;
  /** Stage to open on, so a resumed profile lands where it stopped (create only). */
  initialStep?: ProfileBuilderStep;
  /**
   * Persist the fields belonging to `steps`: the single stage just completed in
   * create mode, every stage in edit mode. Resolving advances (or finishes);
   * rejecting keeps the creator where they are with their input intact, so a
   * failed write is never silently swallowed. Rejecting with a
   * {@link ProfileFieldError} puts the message under the field it names.
   */
  onSave: (draft: CreatorProfileDraft, steps: readonly ProfileBuilderStep[]) => Promise<void>;
  /** Onboarding finished, or an edit saved. */
  onComplete: (draft: CreatorProfileDraft) => void;
  /**
   * Is this handle free? Debounced as the creator types, purely to tell them
   * early — RLS hides unpublished profiles, so a "yes" is a hint rather than a
   * promise, and the database has the last word on submit. Omitted (demo mode,
   * no backend) means no check runs and nothing blocks.
   */
  onCheckHandle?: (handle: string) => Promise<boolean>;
  /**
   * Upload a validated image and resolve its storage key. Omitted (demo mode,
   * no backend) disables the file inputs and leaves the emoji placeholders as
   * the only visuals.
   */
  onUploadImage?: (file: File, kind: 'avatar' | 'portfolio') => Promise<string | null>;
  /** Signed URLs for media already stored, keyed by storage path. */
  mediaUrls?: Record<string, string>;
  /** Leave without saving (edit mode; omitted, no cancel button renders). */
  onCancel?: () => void;
  /** Renders the "nothing is being saved" notice when Supabase is unconfigured. */
  demoMode?: boolean;
}

/** Toggle `value` in `list`, preserving the order things were picked in. */
function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** Split the free-text tag input on commas into trimmed, non-empty tags. */
function parseTags(input: string): string[] {
  return input
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

/** Inline validation message under a field. */
function FieldError({ message }: { message?: string }): JSX.Element | null {
  if (!message) return null;
  return <div className="my-pitch-error">{message}</div>;
}

/** What every field group needs to render and report changes. */
interface FieldGroupProps {
  draft: CreatorProfileDraft;
  errors: DraftErrors;
  update: <K extends keyof CreatorProfileDraft>(key: K, value: CreatorProfileDraft[K]) => void;
}

interface IdentityFieldsProps extends FieldGroupProps {
  handleStatus: HandleStatus;
  normalizedHandle: string;
  /** Signed URL for an already-stored avatar, or a local preview once picked. */
  avatarUrl?: string;
  onPickAvatar: (file: File | null) => void;
  onClearAvatar: () => void;
  uploading: boolean;
  canUpload: boolean;
}

function IdentityFields({
  draft,
  errors,
  update,
  handleStatus,
  normalizedHandle,
  avatarUrl,
  onPickAvatar,
  onClearAvatar,
  uploading,
  canUpload,
}: IdentityFieldsProps): JSX.Element {
  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <label className="form-label" htmlFor="pb-name">
          Display name
        </label>
        <input
          id="pb-name"
          className={`form-input${errors.name ? ' input-error' : ''}`}
          placeholder="e.g. Jordan Reeves"
          value={draft.name}
          onChange={(e) => update('name', e.target.value)}
        />
        <FieldError message={errors.name} />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label className="form-label" htmlFor="pb-handle">
          Handle
        </label>
        <input
          id="pb-handle"
          className={`form-input${errors.handle ? ' input-error' : ''}`}
          placeholder="@jordanreeves"
          value={draft.handle}
          onChange={(e) => update('handle', e.target.value)}
        />
        <FieldError message={errors.handle} />
        {!errors.handle && handleStatus !== 'idle' && (
          <div
            role="status"
            className={handleStatus === 'taken' ? 'my-pitch-error' : 'my-pitch-count'}
          >
            {handleStatus === 'checking'
              ? 'Checking availability…'
              : handleStatus === 'available'
                ? `${normalizedHandle} is available`
                : 'That handle is taken — please choose another.'}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label className="form-label" htmlFor="pb-avatar">
          Profile picture
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 8 }}>
          <div
            className="creator-thumb"
            style={{
              width: 64,
              height: 64,
              minWidth: 64,
              background: DEFAULT_PROFILE_BG,
              borderRadius: 4,
              overflow: 'hidden',
            }}
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Your profile picture"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <span style={{ fontSize: 40 }}>{draft.emoji}</span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <input
              id="pb-avatar"
              type="file"
              accept={ACCEPTED_IMAGE_ATTR}
              disabled={!canUpload || uploading}
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                // Clear the input so re-picking the same file fires onChange
                // again — otherwise a retry after a rejected or failed upload
                // silently does nothing.
                e.target.value = '';
                onPickAvatar(file);
              }}
            />
            {/* Gated on the stored path, not the preview: an avatar whose URL
                failed to sign must still be removable. */}
            {draft.avatarPath && canUpload && (
              <button
                type="button"
                className="btn-ghost"
                style={{ fontSize: 12, padding: '3px 12px', marginLeft: 8 }}
                onClick={onClearAvatar}
                disabled={uploading}
              >
                Remove
              </button>
            )}
          </div>
        </div>
        <div className="my-pitch-count">
          {canUpload
            ? `JPEG, PNG, WebP or GIF, up to ${MAX_IMAGE_LABEL}. Without one, the emoji below stands in.`
            : 'Uploads need a connected account — the emoji below stands in here.'}
        </div>
        <FieldError message={errors.avatarPath} />
      </div>

      <div>
        <label className="form-label">Avatar emoji</label>
        <div className="option-grid">
          {EMOJI_OPTIONS.map((e) => (
            <button
              key={e}
              type="button"
              className={`option-btn${draft.emoji === e ? ' sel' : ''}`}
              aria-pressed={draft.emoji === e}
              aria-label={`Avatar ${e}`}
              onClick={() => update('emoji', e)}
            >
              {e}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

interface FocusFieldsProps extends FieldGroupProps {
  tagInput: string;
  onTagInput: (value: string) => void;
  /** Signed or local preview URLs for portfolio images, keyed by storage path. */
  mediaUrls: Record<string, string>;
  onAddPortfolio: (file: File | null) => void;
  onRemovePortfolio: (path: string) => void;
  uploading: boolean;
  canUpload: boolean;
}

function FocusFields({
  draft,
  errors,
  update,
  tagInput,
  onTagInput,
  mediaUrls,
  onAddPortfolio,
  onRemovePortfolio,
  uploading,
  canUpload,
}: FocusFieldsProps): JSX.Element {
  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <label className="form-label">Primary niche</label>
        <div className="option-grid-2">
          {NICHE_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              className={`option-btn${draft.niche === n ? ' sel' : ''}`}
              aria-pressed={draft.niche === n}
              onClick={() => update('niche', n)}
            >
              {n}
            </button>
          ))}
        </div>
        <FieldError message={errors.niche} />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label className="form-label">Platforms</label>
        <div className="option-grid">
          {PLATFORM_OPTIONS.map((p) => (
            <button
              key={p}
              type="button"
              className={`option-btn${draft.platforms.includes(p) ? ' sel' : ''}`}
              aria-pressed={draft.platforms.includes(p)}
              onClick={() => update('platforms', toggle<Platform>(draft.platforms, p))}
            >
              {p}
            </button>
          ))}
        </div>
        <FieldError message={errors.platforms} />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label className="form-label" htmlFor="pb-tags">
          Tags (optional, up to {MAX_TAGS})
        </label>
        <input
          id="pb-tags"
          className={`form-input${errors.tags ? ' input-error' : ''}`}
          placeholder="UGC, Cinematic, Voiceover"
          value={tagInput}
          onChange={(e) => onTagInput(e.target.value)}
        />
        <FieldError message={errors.tags} />
      </div>

      <div>
        <label className="form-label" htmlFor="pb-portfolio">
          Portfolio images (optional, up to {MAX_PORTFOLIO_IMAGES})
        </label>
        {draft.portfolioPaths.length > 0 && (
          <div className="option-grid" style={{ marginBottom: 8 }}>
            {draft.portfolioPaths.map((path) => (
              <div key={path} style={{ position: 'relative' }}>
                <div
                  className="creator-thumb"
                  style={{
                    height: 72,
                    background: DEFAULT_PROFILE_BG,
                    borderRadius: 4,
                    overflow: 'hidden',
                  }}
                >
                  {mediaUrls[path] && (
                    <img
                      src={mediaUrls[path]}
                      alt="Portfolio work"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  )}
                </div>
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ fontSize: 12, padding: '2px 10px', marginTop: 4, width: '100%' }}
                  aria-label="Remove portfolio image"
                  onClick={() => onRemovePortfolio(path)}
                  disabled={uploading}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
        <input
          id="pb-portfolio"
          type="file"
          accept={ACCEPTED_IMAGE_ATTR}
          disabled={!canUpload || uploading || draft.portfolioPaths.length >= MAX_PORTFOLIO_IMAGES}
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null;
            // Cleared so re-picking the same file fires onChange again.
            e.target.value = '';
            onAddPortfolio(file);
          }}
        />
        <div className="my-pitch-count">
          {canUpload
            ? `Work brands can look at. Without any, your emoji grid stands in.`
            : 'Uploads need a connected account — the emoji grid stands in here.'}
        </div>
        <FieldError message={errors.portfolioPaths} />
      </div>
    </>
  );
}

function OfferFields({ draft, errors, update }: FieldGroupProps): JSX.Element {
  return (
    <>
      <div style={{ marginBottom: 10 }}>
        <label className="form-label" htmlFor="pb-rate">
          Starting rate per deal
        </label>
        <input
          id="pb-rate"
          className={`form-input${errors.rate ? ' input-error' : ''}`}
          placeholder="$ Amount, e.g. 1800"
          inputMode="numeric"
          value={draft.rate}
          onChange={(e) => update('rate', e.target.value)}
        />
        <FieldError message={errors.rate} />
      </div>

      <div>
        <label className="form-label" htmlFor="pb-bio">
          Short bio
        </label>
        <textarea
          id="pb-bio"
          className={`form-input${errors.bio ? ' input-error' : ''}`}
          style={{ resize: 'vertical', minHeight: 120 }}
          placeholder="What you make, who it is for, and what brands get from working with you."
          maxLength={MAX_BIO_CHARS}
          value={draft.bio}
          onChange={(e) => update('bio', e.target.value)}
        />
        <div className="my-pitch-meta">
          <span className="my-pitch-count">
            {draft.bio.length} / {MAX_BIO_CHARS}
          </span>
        </div>
        <FieldError message={errors.bio} />
      </div>
    </>
  );
}

/**
 * The creator profile form (PIT-48, shared with the edit surface in PIT-64).
 *
 * The component owns the draft, the current stage and validation; the caller
 * owns persistence via `onSave`, which is awaited before advancing. In create
 * mode that split is what lets someone who leaves halfway resume the row they
 * already own instead of starting a second one.
 */
export default function ProfileBuilder({
  mode = 'create',
  initialDraft,
  initialStep = 0,
  onSave,
  onComplete,
  onCheckHandle,
  onUploadImage,
  mediaUrls: storedMediaUrls,
  onCancel,
  demoMode = false,
}: ProfileBuilderProps): JSX.Element {
  const editing = mode === 'edit';
  const [draft, setDraft] = useState<CreatorProfileDraft>(initialDraft);
  const [step, setStep] = useState<ProfileBuilderStep>(initialStep);
  const [tagInput, setTagInput] = useState<string>(initialDraft.tags.join(', '));
  const [saving, setSaving] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [handleStatus, setHandleStatus] = useState<HandleStatus>('idle');
  // Populated on a failed submit only, so fields aren't flagged red while the
  // creator is still filling them in for the first time.
  const [errors, setErrors] = useState<DraftErrors>({});

  const normalizedHandle = normalizeHandle(draft.handle);
  // Only ask about a handle that could actually be accepted; checking one the
  // form already rejects would put a second, contradictory message under it.
  const handleIsWellFormed = !validateStep(0, draft).handle;

  // Live availability, debounced so a lookup goes out when typing pauses rather
  // than on every keystroke. Purely informational — submitting does not gate on
  // it, because the definitive answer comes from the unique index on save.
  useEffect(() => {
    if (!onCheckHandle || !handleIsWellFormed) {
      setHandleStatus('idle');
      return undefined;
    }
    let cancelled = false;
    setHandleStatus('checking');
    const timer = setTimeout(() => {
      onCheckHandle(normalizedHandle)
        .then((free) => {
          if (!cancelled) setHandleStatus(free ? 'available' : 'taken');
        })
        .catch(() => {
          // A check that can't complete must not look like a verdict.
          if (!cancelled) setHandleStatus('idle');
        });
    }, HANDLE_CHECK_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [normalizedHandle, handleIsWellFormed, onCheckHandle]);

  const update = <K extends keyof CreatorProfileDraft>(
    key: K,
    value: CreatorProfileDraft[K]
  ): void => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    // Clear the field's error as soon as it is edited — re-validated on submit.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const onTagInput = (value: string): void => {
    setTagInput(value);
    update('tags', parseTags(value));
  };

  // Previews for images picked in this session, keyed by the storage path the
  // upload returned. Local object URLs render instantly and without a round
  // trip; they are revoked on unmount so the blobs don't leak.
  //
  // The ref mirrors the state so the cleanup can read the latest set without
  // depending on it: an effect that depended on `localUrls` would run its
  // cleanup on every change, revoking the first preview the moment a second
  // image was added.
  const [localUrls, setLocalUrls] = useState<Record<string, string>>({});
  const localUrlsRef = useRef<Record<string, string>>({});
  useEffect(
    () => () => {
      Object.values(localUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    },
    []
  );

  const rememberLocalUrl = (path: string, url: string): void => {
    localUrlsRef.current = { ...localUrlsRef.current, [path]: url };
    setLocalUrls(localUrlsRef.current);
  };

  const canUpload = Boolean(onUploadImage);
  const mediaUrls = { ...storedMediaUrls, ...localUrls };

  // Validate, upload, and hand the resulting storage key back to the draft.
  // Validation failures land on the field rather than in a toast the component
  // has no way to raise.
  const uploadImage = async (
    file: File | null,
    kind: 'avatar' | 'portfolio'
  ): Promise<string | null> => {
    const field = kind === 'avatar' ? 'avatarPath' : 'portfolioPaths';
    if (!file || !onUploadImage) return null;
    const check = validateImage(file);
    if (!check.ok) {
      setErrors((prev) => ({ ...prev, [field]: check.error }));
      return null;
    }
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setUploading(true);
    try {
      const path = await onUploadImage(file, kind);
      if (path) rememberLocalUrl(path, URL.createObjectURL(file));
      return path;
    } catch {
      setErrors((prev) => ({
        ...prev,
        [field]: 'Could not upload that image — please try again.',
      }));
      return null;
    } finally {
      setUploading(false);
    }
  };

  const pickAvatar = async (file: File | null): Promise<void> => {
    const path = await uploadImage(file, 'avatar');
    if (path) update('avatarPath', path);
  };

  const addPortfolio = async (file: File | null): Promise<void> => {
    if (draft.portfolioPaths.length >= MAX_PORTFOLIO_IMAGES) return;
    const path = await uploadImage(file, 'portfolio');
    if (path) update('portfolioPaths', [...draft.portfolioPaths, path]);
  };

  // Removing only detaches the media from the profile. The stored object is the
  // caller's to clean up on save — deleting here would strand a creator who
  // changed their mind before saving.
  const clearAvatar = (): void => update('avatarPath', null);
  const removePortfolio = (path: string): void =>
    update(
      'portfolioPaths',
      draft.portfolioPaths.filter((p) => p !== path)
    );

  // Edit saves everything at once; onboarding saves the stage just finished.
  const stepsToSave: readonly ProfileBuilderStep[] = editing ? ALL_STEPS : [step];

  const submit = async (): Promise<void> => {
    if (saving) return;
    const found = validateSteps(draft, stepsToSave);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft, stepsToSave);
      if (editing || step === LAST_STEP) {
        onComplete(draft);
        return;
      }
      setStep((prev) => (prev + 1) as ProfileBuilderStep);
      setErrors({});
    } catch (err) {
      // A field the database itself rejected — a handle claimed between the
      // availability check and this submit, say. Putting the message back on
      // the input that caused it beats a toast the creator has to interpret.
      // Anything else is the caller's to surface; staying put preserves what
      // was typed so it can be retried either way.
      if (err instanceof ProfileFieldError) {
        setErrors((prev) => ({ ...prev, [err.field]: err.message }));
        if (err.field === 'handle') setHandleStatus('taken');
      }
    } finally {
      setSaving(false);
    }
  };

  const back = (): void => {
    if (saving || step === 0) return;
    setStep((prev) => (prev - 1) as ProfileBuilderStep);
    setErrors({});
  };

  const identity = (
    <IdentityFields
      draft={draft}
      errors={errors}
      update={update}
      handleStatus={handleStatus}
      normalizedHandle={normalizedHandle}
      avatarUrl={draft.avatarPath ? mediaUrls[draft.avatarPath] : undefined}
      onPickAvatar={pickAvatar}
      onClearAvatar={clearAvatar}
      uploading={uploading}
      canUpload={canUpload}
    />
  );
  const focus = (
    <FocusFields
      draft={draft}
      errors={errors}
      update={update}
      tagInput={tagInput}
      onTagInput={onTagInput}
      mediaUrls={mediaUrls}
      onAddPortfolio={addPortfolio}
      onRemovePortfolio={removePortfolio}
      uploading={uploading}
      canUpload={canUpload}
    />
  );
  const offer = <OfferFields draft={draft} errors={errors} update={update} />;
  const groups = [identity, focus, offer];

  const submitLabel = editing ? 'Save Changes' : step === LAST_STEP ? 'Finish Profile' : 'Continue';

  return (
    <div className="view">
      <div className="section-header">
        {editing ? 'Edit your profile' : 'Build your creator profile'}
      </div>
      <div className="section-sub" style={{ marginBottom: 20 }}>
        {editing
          ? 'Change anything here and brands see it the next time they open your profile. Your publish state is unaffected.'
          : 'Brands browse Discover by niche, platform and rate. This is what they will see — you can refine it any time, and nothing is public until you publish it.'}
      </div>

      {demoMode && (
        <div className="section-sub" style={{ marginBottom: 20 }} role="note">
          {editing
            ? 'Demo mode — edit freely; nothing is saved.'
            : 'Demo mode — walk through the whole flow; nothing is saved.'}
        </div>
      )}

      {/* Stage indicator — onboarding only; editing shows everything at once. */}
      {!editing && (
        <div className="option-grid" style={{ marginBottom: 20 }}>
          {PROFILE_BUILDER_STEPS.map((s, i) => (
            <div key={s.title} className={`option-btn${i === step ? ' sel' : ''}`}>
              {i + 1}. {s.title}
            </div>
          ))}
        </div>
      )}

      <div className="panel">
        {editing ? (
          PROFILE_BUILDER_STEPS.map((s, i) => (
            <div className="form-section" key={s.title}>
              <div className="form-section-label">{s.title}</div>
              <div className="section-sub" style={{ marginBottom: 16 }}>
                {s.hint}
              </div>
              {groups[i]}
            </div>
          ))
        ) : (
          <div className="form-section">
            <div className="form-section-label">{PROFILE_BUILDER_STEPS[step].title}</div>
            <div className="section-sub" style={{ marginBottom: 16 }}>
              {PROFILE_BUILDER_STEPS[step].hint}
            </div>
            {groups[step]}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          {!editing && step > 0 && (
            <button type="button" className="btn-outline" onClick={back} disabled={saving}>
              Back
            </button>
          )}
          {editing && onCancel && (
            <button type="button" className="btn-outline" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className="btn-submit-gold"
            onClick={submit}
            disabled={saving}
            style={{ flex: 1 }}
          >
            {saving ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
